import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCollageEditor, type TextLayerState, type EditorPhoto } from './useCollageEditor';
import type { CollageLayout } from '../../utils/collageLayouts';
import type { CollageTheme } from '../../utils/collageThemes';
import type { EventAudio } from '../../types';
import { preloadTranscriptionModel, transcribeAudioClip } from '../../utils/transcribeAudio';
import { BetaBadge } from '../../components/BetaBadge';

const TEXT_COLORS = ['#ffffff', '#1e1b3a', '#f59e0b', '#f472b6'];

// This page's content scrolls with the whole document (Layout.tsx has no
// fixed-height scroll container around page content), so `position: sticky`
// only sticks within its own parent's bounds — on a long Select/Arrange step
// that just means "sits at the end of the content", not "floats at the
// bottom of the viewport". Use a real `fixed` bar instead, matched to this
// page's own max-w-3xl column, with a wrapper `pb-24` above it so the last
// row of content isn't hidden underneath.
function FixedBottomBar({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#F8F9FF] border-t border-indigo-100 px-4 lg:px-8" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="max-w-3xl mx-auto pt-3 pb-3 flex flex-col gap-2">
        {children}
      </div>
    </div>
  );
}

export function CollageEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editor = useCollageEditor({ collageId: id });
  const [arrangeToast, setArrangeToast] = useState<string | null>(null);

  if (editor.loading) return <p className="text-slate-500">Loading…</p>;

  async function handleSaveAndDone() {
    const saved = await editor.saveAndRenderIfGrid();
    navigate(`/events/collages/${saved.id}`);
  }

  function handleAutoArrange() {
    const { layoutName, themeName } = editor.autoArrange();
    setArrangeToast(`✨ Picked ${layoutName} + ${themeName} based on your ${editor.selected.length} photos' dates`);
    setTimeout(() => setArrangeToast(null), 3600);
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">🖼️</span>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{id ? 'Edit Collage' : 'New Collage'}</h1>
      </div>

      <div className="flex gap-1 mb-6 border-b border-indigo-50">
        {(['select', 'arrange', 'preview'] as const).map((step, i) => (
          <button
            key={step}
            type="button"
            disabled={step !== 'select' && editor.selected.length === 0}
            onClick={() => editor.setPhase(step)}
            className={`px-3 py-2 text-sm font-bold border-b-2 -mb-px transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              editor.phase === step ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-50 text-[10px] mr-1.5 align-middle">{i + 1}</span>
            {step === 'select' ? 'Select' : step === 'arrange' ? 'Arrange' : 'Preview'}
          </button>
        ))}
      </div>

      {editor.phase === 'select' && (
        <SelectStep editor={editor} />
      )}

      {editor.phase === 'arrange' && (
        <ArrangeStep editor={editor} toast={arrangeToast} onAutoArrange={handleAutoArrange} onSave={handleSaveAndDone} />
      )}

      {editor.phase === 'preview' && (
        <PreviewStep editor={editor} onSave={handleSaveAndDone} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Select step
// ---------------------------------------------------------------------------

function SelectStep({ editor }: { editor: ReturnType<typeof useCollageEditor> }) {
  const eventIds = editor.eventGroups.map(g => g.eventId);
  return (
    <div className="flex flex-col gap-4 pb-24">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => editor.autoFillFromEvents(eventIds, editor.layout.maxPhotos)}
          className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-sm font-bold px-3 py-2 rounded-lg hover:bg-indigo-100 transition-colors"
        >
          <span>✨</span> Auto-fill from these events
        </button>
        {/* Also reachable without scrolling — the earlier bottom-only button
            forced users to scroll past every event's photos to continue,
            even right after selecting from the very first event. */}
        <button
          type="button"
          disabled={editor.selected.length === 0}
          onClick={editor.goToArrange}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-bold shadow-sm shadow-indigo-200 transition-colors"
        >
          Arrange {editor.selected.length} selected photo{editor.selected.length === 1 ? '' : 's'}
        </button>
      </div>

      {editor.eventGroups.length === 0 ? (
        <p className="text-slate-400 text-sm">No photos found — add some events with photos first.</p>
      ) : (
        editor.eventGroups.map(group => (
          <div key={group.eventId}>
            <p className="text-xs font-bold text-slate-500 mb-2">{group.eventTitle}</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {group.photos.map(photo => {
                const isSelected = editor.selected.includes(photo.id);
                return (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => editor.togglePhoto(photo.id)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 ${isSelected ? 'border-indigo-600' : 'border-transparent'}`}
                  >
                    <img src={photo.filePath} alt="" loading="lazy" className="w-full h-full object-cover" />
                    <span className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      isSelected ? 'bg-indigo-600 text-white' : 'bg-white/80 text-transparent border border-slate-300'
                    }`}>✓</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      <FixedBottomBar>
        <button
          type="button"
          disabled={editor.selected.length === 0}
          onClick={editor.goToArrange}
          className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl text-sm font-bold shadow-md shadow-indigo-200 transition-colors"
        >
          Arrange {editor.selected.length} selected photo{editor.selected.length === 1 ? '' : 's'}
        </button>
      </FixedBottomBar>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Arrange step
// ---------------------------------------------------------------------------

function ArrangeStep({
  editor, toast, onAutoArrange, onSave,
}: {
  editor: ReturnType<typeof useCollageEditor>;
  toast: string | null;
  onAutoArrange: () => void;
  onSave: () => void;
}) {
  const [transcribePhotoId, setTranscribePhotoId] = useState<string | null>(null);
  const [recordingsPickerOpen, setRecordingsPickerOpen] = useState(false);
  const transcribeTarget = transcribePhotoId ? editor.photoById.get(transcribePhotoId) : undefined;

  // A per-photo badge here was nearly invisible in practice (a plain 24px
  // dark circle in a photo's corner, easy to mistake for part of the photo
  // itself) — surface it as a single labeled toolbar entry instead, next to
  // "+ Add text", which is exactly where users already look for collage
  // text tools.
  const recordablePhotos = editor.selected
    .map(id => editor.photoById.get(id))
    .filter((p): p is EditorPhoto => Boolean(p?.hasAudio));

  function openRecordings() {
    if (recordablePhotos.length === 1) setTranscribePhotoId(recordablePhotos[0].id);
    else setRecordingsPickerOpen(true);
  }

  return (
    <div className="flex flex-col gap-4 pb-24">
      {transcribeTarget && (
        <TranscribeModal
          photo={transcribeTarget}
          clips={editor.audioClipsByEvent[transcribeTarget.eventId] ?? []}
          onApply={(text, audioId) => editor.setPhotoTranscript(transcribeTarget.id, text, audioId)}
          onClose={() => setTranscribePhotoId(null)}
        />
      )}
      {recordingsPickerOpen && (
        <RecordingsPickerModal
          photos={recordablePhotos}
          onPick={photoId => { setRecordingsPickerOpen(false); setTranscribePhotoId(photoId); }}
          onClose={() => setRecordingsPickerOpen(false)}
        />
      )}
      <div className="flex bg-indigo-50 rounded-xl p-1">
        {(['grid', 'storyboard'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => editor.setType(t)}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-colors ${editor.type === t ? 'bg-white text-indigo-700 shadow-sm' : 'text-indigo-500'}`}
          >
            {t === 'grid' ? '▦ Grid' : '☰ Storyboard'}
          </button>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={onAutoArrange}
          className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-sm font-bold px-3 py-2 rounded-lg hover:bg-indigo-100 transition-colors"
        >
          <span>✨</span> Auto-arrange
        </button>
        {editor.type === 'grid' && (
          <button
            type="button"
            onClick={editor.addTextLayer}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-sm font-bold px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            ＋ Add text
          </button>
        )}
        {recordablePhotos.length > 0 && (
          <button
            type="button"
            onClick={openRecordings}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-sm font-bold px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            🎙️ Event recordings <BetaBadge />
          </button>
        )}
      </div>
      {toast && <p className="text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-lg px-3 py-2">{toast}</p>}

      <input
        value={editor.title}
        onChange={e => editor.setTitle(e.target.value)}
        placeholder="Give this collage a title…"
        className="w-full border border-indigo-100 rounded-lg px-3 py-2.5 text-sm font-bold text-slate-900 placeholder:font-medium placeholder:text-slate-400"
      />

      {editor.type === 'grid' ? (
        <GridArrange editor={editor} />
      ) : (
        <StoryboardArrange editor={editor} />
      )}

      <FixedBottomBar>
        <button
          type="button"
          onClick={async () => { await editor.goToPreview(); }}
          className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-md shadow-indigo-200 transition-colors"
        >
          Preview collage
        </button>
        <button type="button" onClick={onSave} className="text-xs font-semibold text-slate-500 hover:text-slate-700">
          Save without previewing
        </button>
        <button type="button" onClick={() => editor.setPhase('select')} className="w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors">
          ← Back to Select
        </button>
      </FixedBottomBar>
    </div>
  );
}

function LayoutSwatch({ layout, active, onClick }: { layout: CollageLayout; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex-shrink-0 w-16 text-center">
      <div className={`relative w-16 h-12 rounded-lg border-2 bg-white overflow-hidden ${active ? 'border-indigo-600' : 'border-transparent'}`}>
        {layout.slots.map((s, i) => (
          <div key={i} className="absolute bg-indigo-100 border border-white"
            style={{ left: `${s.x}%`, top: `${s.y}%`, width: `${s.w}%`, height: `${s.h}%` }} />
        ))}
      </div>
      <span className={`text-[11px] block mt-1 ${active ? 'text-indigo-700 font-bold' : 'text-slate-500'}`}>{layout.name}</span>
    </button>
  );
}

function ThemeSwatch({ theme, active, onClick }: { theme: CollageTheme; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex-shrink-0 w-16 text-center">
      <div
        className={`w-16 h-12 rounded-lg border-2 ${active ? 'border-indigo-600' : 'border-transparent'}`}
        style={{ background: theme.canvasBackground }}
      />
      <span className={`text-[11px] block mt-1 ${active ? 'text-indigo-700 font-bold' : 'text-slate-500'}`}>{theme.name}</span>
    </button>
  );
}

// Drag-to-reposition crop: `object-fit: cover` always fills the frame, but a
// fixed centered crop (the only option before this) routinely cut off the
// interesting part of a photo — a face, a sign, anything not dead-center.
// `object-position` lets the browser do the actual crop math natively; this
// component only needs to convert a drag gesture into a focal-point
// percentage. Dragging the photo right reveals more of its left side, so the
// focal point moves opposite the drag direction — same feel as any
// photo-editor's "pan to crop" gesture.
function CroppablePhoto({
  photo, focal, onFocalChange, filterCss, className = '',
}: {
  photo: EditorPhoto;
  focal: { x: number; y: number };
  onFocalChange: (x: number, y: number) => void;
  filterCss: string;
  className?: string;
}) {
  function onPointerDown(e: React.PointerEvent<HTMLImageElement>) {
    e.stopPropagation(); // don't let a slot's own onClick (tap-to-place) fire from this drag
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startFocal = focal;
    let moved = false;

    function handleMove(ev: PointerEvent) {
      const dxPct = ((ev.clientX - startX) / rect.width) * 100;
      const dyPct = ((ev.clientY - startY) / rect.height) * 100;
      if (Math.abs(dxPct) > 1 || Math.abs(dyPct) > 1) moved = true;
      onFocalChange(startFocal.x - dxPct, startFocal.y - dyPct);
    }
    function handleUp() {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      // A real drag shouldn't also register as the trailing "tap to place" click.
      if (moved) el.addEventListener('click', ce => { ce.stopPropagation(); ce.preventDefault(); }, { capture: true, once: true });
    }
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  }

  return (
    <img
      src={photo.filePath}
      alt=""
      onPointerDown={onPointerDown}
      className={`w-full h-full object-cover cursor-move touch-none ${className}`}
      style={{ filter: filterCss, objectPosition: `${focal.x}% ${focal.y}%` }}
    />
  );
}

function GridArrange({ editor }: { editor: ReturnType<typeof useCollageEditor> }) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Layout</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {editor.layouts.map(l => (
            <LayoutSwatch key={l.id} layout={l} active={editor.layoutId === l.id} onClick={() => editor.selectLayout(l.id)} />
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Theme</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {editor.themes.map(t => (
            <ThemeSwatch key={t.id} theme={t} active={editor.themeId === t.id} onClick={() => editor.setThemeId(t.id)} />
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-500">Tap a photo below, then tap a slot to place it. Drag a placed photo to reposition its crop. Drag any text to reposition it, tap it to edit or restyle.</p>

      <div
        className="relative rounded-xl overflow-hidden shadow-md"
        style={{ aspectRatio: editor.layout.aspect, background: editor.theme.canvasBackground }}
      >
        {editor.layout.slots.map((slot, idx) => {
          const photoId = editor.slotFill[idx];
          const photo = photoId ? editor.photoById.get(photoId) : undefined;
          return (
            <div
              key={idx}
              role="button"
              tabIndex={0}
              onClick={() => editor.placeInSlot(idx)}
              onKeyDown={e => { if (e.key === 'Enter') editor.placeInSlot(idx); }}
              className={`absolute overflow-hidden flex items-end cursor-pointer ${photo ? '' : 'border-2 border-dashed border-slate-300 bg-slate-100/40'}`}
              style={{ left: `${slot.x}%`, top: `${slot.y}%`, width: `${slot.w}%`, height: `${slot.h}%` }}
            >
              {photo ? (
                <>
                  <CroppablePhoto
                    photo={photo}
                    focal={editor.getPhotoFocal(photo.id)}
                    onFocalChange={(x, y) => editor.setPhotoFocal(photo.id, x, y)}
                    filterCss={editor.theme.previewFilterCss}
                    className="absolute inset-0"
                  />
                  {editor.captions[photo.id] && (
                    <span className="relative z-10 w-full text-[10px] px-1.5 py-1 bg-black/40 text-white text-left truncate">
                      {editor.captions[photo.id]}
                    </span>
                  )}
                </>
              ) : (
                <span className="m-auto text-[10px] font-bold text-slate-400">{editor.armedPhotoId ? 'Tap to place' : '+ empty'}</span>
              )}
            </div>
          );
        })}
        {editor.textLayers.map(layer => (
          <TextLayerHandle key={layer.id} layer={layer} editor={editor} />
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Your photos</p>
        <p className="text-[11px] font-semibold text-slate-400">{editor.armedPhotoId ? 'now tap a slot →' : 'tap one, then tap a slot'}</p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {editor.selected.map(id => {
          const photo = editor.photoById.get(id);
          if (!photo) return null;
          const used = Object.values(editor.slotFill).includes(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => editor.armPhoto(id)}
              className={`relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 ${
                editor.armedPhotoId === id ? 'border-amber-500' : 'border-transparent'
              } ${used ? 'opacity-40' : ''}`}
            >
              <img src={photo.filePath} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TextLayerHandle({ layer, editor }: { layer: TextLayerState; editor: ReturnType<typeof useCollageEditor> }) {
  const selected = editor.selectedTextId === layer.id;
  const sizeClass = layer.size === 's' ? 'text-xs' : layer.size === 'l' ? 'text-2xl' : 'text-base';

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    const container = e.currentTarget.parentElement;
    if (!container) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    let moved = false;

    function handleMove(ev: PointerEvent) {
      moved = true;
      const rect = container!.getBoundingClientRect();
      const x = Math.max(6, Math.min(94, ((ev.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(6, Math.min(94, ((ev.clientY - rect.top) / rect.height) * 100));
      editor.moveTextLayer(layer.id, x, y);
    }
    function handleUp() {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      if (!moved) editor.setSelectedTextId(selected ? null : layer.id);
    }
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  }

  return (
    <div
      onPointerDown={onPointerDown}
      className={`absolute z-20 -translate-x-1/2 -translate-y-1/2 px-2 py-1 font-bold cursor-grab select-none ${sizeClass} ${selected ? 'outline outline-2 outline-dashed outline-indigo-400' : ''}`}
      style={{ left: `${layer.xPct}%`, top: `${layer.yPct}%`, color: layer.color }}
    >
      {layer.text}
      {selected && (
        <div
          onPointerDown={e => e.stopPropagation()}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 bg-slate-900 rounded-lg px-1.5 py-1 flex items-center gap-1 shadow-lg whitespace-nowrap"
        >
          <button type="button" onClick={() => {
            const next = window.prompt('Text:', layer.text);
            if (next !== null) editor.updateTextLayer(layer.id, { text: next });
          }} className="w-6 h-6 rounded-md bg-white/10 text-white text-xs">✎</button>
          {(['s', 'm', 'l'] as const).map(sz => (
            <button key={sz} type="button" onClick={() => editor.updateTextLayer(layer.id, { size: sz })}
              className="w-6 h-6 rounded-md bg-white/10 text-white text-xs uppercase">{sz}</button>
          ))}
          {TEXT_COLORS.map(c => (
            <button key={c} type="button" onClick={() => editor.updateTextLayer(layer.id, { color: c })}
              className="w-4 h-4 rounded-full border border-white/50" style={{ background: c }} />
          ))}
          <button type="button" onClick={() => editor.removeTextLayer(layer.id)} className="w-6 h-6 rounded-md bg-red-500/40 text-white text-xs">✕</button>
        </div>
      )}
    </div>
  );
}

function StoryboardArrange({ editor }: { editor: ReturnType<typeof useCollageEditor> }) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Theme</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {editor.themes.map(t => (
            <ThemeSwatch key={t.id} theme={t} active={editor.themeId === t.id} onClick={() => editor.setThemeId(t.id)} />
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">Reorder with the arrows — each card keeps its photo's own date and an editable caption.</p>
      <div className="flex flex-col">
        {editor.storyOrder.map((photoId, i) => {
          const photo = editor.photoById.get(photoId);
          if (!photo) return null;
          return (
            <div key={photoId} className="flex gap-3 relative pb-5">
              {i < editor.storyOrder.length - 1 && (
                <div className="absolute left-[6px] top-[26px] bottom-0 w-0.5 bg-indigo-100" />
              )}
              <div className="w-3.5 h-3.5 rounded-full bg-indigo-600 shadow-[0_0_0_4px_#eef2ff] mt-1 flex-shrink-0" />
              <div className="flex-1 bg-white rounded-xl border border-indigo-50 shadow-sm overflow-hidden">
                <div className="relative aspect-[16/10]">
                  <CroppablePhoto
                    photo={photo}
                    focal={editor.getPhotoFocal(photo.id)}
                    onFocalChange={(x, y) => editor.setPhotoFocal(photo.id, x, y)}
                    filterCss={editor.theme.previewFilterCss}
                    className="absolute inset-0"
                  />
                </div>
                <div className="p-3 flex gap-2 items-start">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-indigo-600">
                      {photo.takenAt ? new Date(photo.takenAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'Undated'}
                    </p>
                    <input
                      value={editor.captions[photoId] ?? ''}
                      onChange={e => editor.setCaption(photoId, e.target.value)}
                      placeholder="Caption…"
                      className="text-sm text-slate-900 mt-0.5 w-full border-b border-transparent hover:border-slate-200 focus:border-indigo-300 focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button type="button" disabled={i === 0} onClick={() => editor.moveStoryItem(i, -1)}
                      className="w-5 h-5 rounded border border-slate-200 text-slate-500 text-xs disabled:opacity-30">↑</button>
                    <button type="button" disabled={i === editor.storyOrder.length - 1} onClick={() => editor.moveStoryItem(i, 1)}
                      className="w-5 h-5 rounded border border-slate-200 text-slate-500 text-xs disabled:opacity-30">↓</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview step
// ---------------------------------------------------------------------------

function PreviewStep({ editor, onSave }: { editor: ReturnType<typeof useCollageEditor>; onSave: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      {editor.type === 'grid' ? (
        editor.rendering ? (
          <div className="aspect-[4/5] rounded-xl bg-slate-100 animate-pulse flex items-center justify-center text-slate-400 text-sm">Rendering…</div>
        ) : editor.renderedPreviewUrl ? (
          <img src={editor.renderedPreviewUrl} alt="Collage preview" className="w-full rounded-xl shadow-lg" />
        ) : (
          <p className="text-slate-400 text-sm">Preview not rendered yet.</p>
        )
      ) : (
        <div className="flex flex-col gap-3 bg-white rounded-xl border border-indigo-50 p-4">
          {editor.title && <h2 className="font-bold text-lg text-slate-900">{editor.title}</h2>}
          {editor.storyOrder.map(photoId => {
            const photo = editor.photoById.get(photoId);
            if (!photo) return null;
            return (
              <div key={photoId} className="flex gap-3">
                <img src={photo.filePath} alt="" loading="lazy" className="w-24 h-24 rounded-lg object-cover flex-shrink-0" style={{ filter: editor.theme.previewFilterCss }} />
                <div>
                  <p className="text-[11px] font-bold text-indigo-600">
                    {photo.takenAt ? new Date(photo.takenAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'Undated'}
                  </p>
                  <p className="text-sm text-slate-700">{editor.captions[photoId]}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={onSave} className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-md shadow-indigo-200 transition-colors">
          Save collage
        </button>
        <button type="button" onClick={() => editor.setPhase('arrange')} className="px-4 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors">
          ← Back
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Picks which selected photo a recording should become the caption for —
// only shown when more than one selected photo has audio; with exactly one,
// the toolbar button skips straight to TranscribeModal for it.
// ---------------------------------------------------------------------------

function RecordingsPickerModal({
  photos, onPick, onClose,
}: {
  photos: EditorPhoto[];
  onPick: (photoId: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
            Event recordings <BetaBadge />
          </p>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">✕</button>
        </div>
        <div className="p-5 flex flex-col gap-1 overflow-y-auto">
          <p className="text-xs text-slate-500 mb-2">Which photo's caption should a recording be converted to?</p>
          {photos.map(photo => (
            <button
              key={photo.id}
              type="button"
              onClick={() => onPick(photo.id)}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 text-left transition-colors"
            >
              <img src={photo.filePath} alt="" loading="lazy" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
              <span className="text-sm font-semibold text-slate-700">{photo.eventTitle}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Include recording as text" modal — see the plan's note on why this offers
// the photo's *event* recordings (there's no photo-level audio link in the
// data model), why it's always review-gated (never silently applied), and
// why whisper-tiny.en's accuracy needs the BetaBadge here.
// ---------------------------------------------------------------------------

function TranscribeModal({
  photo, clips, onApply, onClose,
}: {
  photo: EditorPhoto;
  clips: EventAudio[];
  onApply: (text: string, audioId: string) => void;
  onClose: () => void;
}) {
  const [transcribingId, setTranscribingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { preloadTranscriptionModel(); }, []);

  async function handleTranscribe(clip: EventAudio) {
    setTranscribingId(clip.id);
    setError(null);
    try {
      const text = await transcribeAudioClip(clip.filePath);
      if (!text) {
        setError("Couldn't make out any words in this recording — try a clearer clip, or type the caption yourself.");
        return;
      }
      onApply(text, clip.id);
      onClose();
    } catch {
      setError('Transcription failed — please try again.');
    } finally {
      setTranscribingId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
            Include recording as text <BetaBadge />
          </p>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">✕</button>
        </div>
        <div className="p-5 flex flex-col gap-3 overflow-y-auto">
          <p className="text-xs text-slate-500">
            From <span className="font-semibold">{photo.eventTitle}</span> — pick a recording to turn into this photo's caption. You can always edit the result afterward.
          </p>
          {error && <p className="text-xs text-red-500 bg-red-50 rounded-lg px-2.5 py-2">{error}</p>}
          {clips.length === 0 ? (
            <p className="text-sm text-slate-400">No recordings on this event.</p>
          ) : (
            clips.map(clip => (
              <div key={clip.id} className="border border-slate-100 rounded-xl p-3 flex flex-col gap-2">
                <p className="text-xs font-semibold text-slate-700">{clip.label || 'Voice recording'}</p>
                <audio controls src={clip.filePath} className="w-full h-9" />
                <button
                  type="button"
                  disabled={transcribingId === clip.id}
                  onClick={() => handleTranscribe(clip)}
                  className="self-start px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors"
                >
                  {transcribingId === clip.id ? 'Transcribing…' : 'Include as text'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
